import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, Input, OnDestroy, OnInit, signal, ViewChild, WritableSignal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { DialogModule } from "primeng/dialog";
import { debounceTime, distinctUntilChanged, Subject, takeUntil, tap } from "rxjs";
import { AppService } from "../services/app.service";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { Router } from "@angular/router";
import { GlobalServicesService } from "../services/global-services.service";
import { TalentPoolService } from "../services/talent-pool.service";

type SearchCategory = "all" | "event" | "role" | "candidates";

@Component({
    selector: 'app-spotlight-search',
    templateUrl: './spotlight-search.component.html',
    styleUrls: ['./spotlight-search.component.css'],
    imports: [DialogModule, ReactiveFormsModule, ProgressSpinnerModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SpotlightSearchComponent implements OnInit, OnDestroy {
    unsub$: Subject<void> = new Subject<void>();
    cancelSearch$: Subject<void> = new Subject<void>();

    SEARCH_MIN_LENGTH = 3;
    readonly PAGE_SIZE = 10;

    @Input() showSpotlightSearch$: Subject<void> = new Subject<void>();
    @ViewChild("resultsContainer") resultsContainer?: ElementRef<HTMLElement>;

    searchForm!: FormGroup;
    activeSearchCategory: WritableSignal<SearchCategory> = signal("all");

    events: any[] = [];
    candidates: any[] = [];
    roles: any[] = [];
    loading: boolean = false;
    loadingMore: boolean = false;
    hasMore: boolean = false;
    private currentPage: number = 1;
    showSearch: boolean = false;
    firstApiCalled: boolean = false;

    userRole: string = "";
    openingCandidateEmail: string = "";

    constructor(
        private fb: FormBuilder,
        private appService: AppService,
        private router: Router,
        private globalService: GlobalServicesService,
        private talentPoolService: TalentPoolService
    ) { }

    ngOnInit(): void {
        this.userRole = this.globalService.privilegeRoles;

        this.searchForm = this.fb.group({
            search: [""]
        })

        this.searchForm.get("search")?.valueChanges.pipe(takeUntil(this.unsub$), distinctUntilChanged(), tap(() => this.hasMore = false), debounceTime(500)).subscribe((val) => {
            this.firstApiCalled = true;
            this.globalSearch(val);

            if (val.length < 3) {
                this.firstApiCalled = false;
            }
        })

        this.showSpotlightSearch$.pipe(takeUntil(this.unsub$)).subscribe(() => {
            this.activeSearchCategory.set("all");
            this.showSearch = true;
        })
    }

    ngOnDestroy(): void {
        this.unsub$.next();
        this.unsub$.complete();

        this.cancelSearch$.next();
        this.cancelSearch$.complete();
    }

    changeActiveSearchCategory(category: SearchCategory) {
        this.activeSearchCategory.set(category);
        this.globalSearch(this.searchForm.get("search")?.value)
    }

    globalSearch(val: string) {
        this.events = [];
        this.candidates = [];
        this.roles = [];
        this.hasMore = false;
        this.currentPage = 1;

        if (!val || val?.length < this.SEARCH_MIN_LENGTH) {
            return;
        }

        this.fetchResults(val, false);
    }

    onResultsScroll(event: any) {
        const element = event.target;
        const threshold = 100;

        if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
            if (this.hasMore && !this.loadingMore && !this.loading) {
                this.fetchResults(this.searchForm.get("search")?.value, true);
            }
        }
    }

    private fetchResults(val: string, append: boolean) {
        if (!val || val.length < this.SEARCH_MIN_LENGTH) { return; }

        if (!append) { this.cancelSearch$.next(); }

        const params: any = { query: val, size: this.PAGE_SIZE };
        if (this.activeSearchCategory() !== "all") {
            params.required_type = this.activeSearchCategory();
        }
        if (append) {
            params.page = this.currentPage + 1;
            this.loadingMore = true;
        } else {
            this.loading = true;
        }

        this.appService.globalSearch(params).pipe(takeUntil(this.cancelSearch$)).subscribe((res) => {
            const response: any = res.body;
            if (response) {
                const events = response.events || [];
                const candidates = response.candidates || [];
                const roles = response.job_roles || [];

                this.events = append ? [...this.events, ...events] : events;
                this.candidates = append ? [...this.candidates, ...candidates] : candidates;
                this.roles = append ? [...this.roles, ...roles] : roles;

                this.currentPage = append ? this.currentPage + 1 : 1;
                this.hasMore = !!(response.has_more?.events
                    || response.has_more?.candidates
                    || response.has_more?.job_roles);
            }
            this.loading = false;
            this.loadingMore = false;
            this.loadNextPageIfNotScrollable();
        }, () => {
            this.loading = false;
            this.loadingMore = false;
        })
    }

    private loadNextPageIfNotScrollable() {
        setTimeout(() => {
            const element = this.resultsContainer?.nativeElement;
            if (!element || !this.hasMore || this.loading || this.loadingMore) { return; }

            if (element.scrollHeight <= element.clientHeight) {
                this.fetchResults(this.searchForm.get("search")?.value, true);
            }
        });
    }

    clearSearch() {
        this.searchForm.get("search")?.setValue("");
        this.cancelSearch$.next();
    }

    closeSearch() {
        this.showSearch = false;
        this.firstApiCalled = false;
        this.clearSearch();
    }

    openEvent(event: any) {
        this.closeSearch();
        const viewParam = this.userRole === 'interviewer' || this.userRole === 'evaluator' ? 'workflow' : 'overview';
        this.router.navigate(['events/', event.event_id], { queryParams: { view: viewParam }, state: { eventName: event.name } });
    }

    openRole(role: any) {
        this.closeSearch();
        this.router.navigate(["/roles", role.role_id])
    }

    openCandidate(candidate: any) {
        if (this.openingCandidateEmail) {
            return;
        }

        this.openingCandidateEmail = candidate.email;
        this.talentPoolService.getAllCandidatesFastApi(1, 10, "desc", "uuid", candidate.email).subscribe({
            next: (response: any) => {
                const items: any[] = response?.body?.items || [];
                if (items?.length) {
                    const currentSelectedCandidate = items.find((apiCandidate) => apiCandidate.uuid === candidate.candidate_id);
                    if (currentSelectedCandidate) {
                        this.closeSearch();
                        this.router.navigate(["/candidates"]);
                        this.appService.spotlightSearchCandidate.next(currentSelectedCandidate);
                    }
                }
                this.openingCandidateEmail = "";
            },
            error: () => {
                this.openingCandidateEmail = "";
            }
        });
    }

    @HostListener('document:keydown.escape', ['$event'])
    handleEscape(event: KeyboardEvent) {
        if (this.showSearch) {
            this.closeSearch();
            event.preventDefault();
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;

        const dialogMask = target.closest('.p-dialog-mask');
        const insideDialog = target.closest('.p-dialog') || target.closest('.p-component');

        if (this.showSearch && dialogMask && !insideDialog && !this.searchForm.get("search")?.value) {
            this.closeSearch();
        }
    }

}